import React, { useRef, useState } from 'react';
import { FiX, FiInfo, FiDownload, FiMaximize2, FiMinimize2, FiHelpCircle, FiRefreshCw, FiRotateCcw } from 'react-icons/fi';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ZAxis, ReferenceLine
} from 'recharts';
import WordCloudChart from './WordCloudChart';
import { useNLPLogs } from '../../context/NLPLogsContext';
import { useNLPCache } from '../../context/NLPCacheContext';
import { useNLPProcessing } from '../../hooks/useNLPProcessing';
import './ContextualAnalytics.css';

// Professional, vibrant color palette (12 colors for better data viz)
const COLORS = [
  '#2a5298', // Vibrant Blue
  '#7c3aed', // Purple
  '#db2777', // Pink
  '#dc2626', // Red
  '#ea580c', // Orange
  '#ca8a04', // Gold
  '#16a34a', // Green
  '#0891b2', // Cyan
  '#4f46e5', // Indigo
  '#9333ea', // Violet
  '#e11d48', // Rose
  '#0d9488'  // Teal
];

// Extended palette for >12 categories (adds softer tones)
const COLORS_EXTENDED = [
  ...COLORS,
  '#4a7bc7', // Light Blue
  '#a78bfa', // Light Purple
  '#f472b6', // Light Pink
  '#fb923c', // Light Orange
  '#4ade80', // Light Green
  '#22d3ee'  // Light Cyan
];

// Helper to truncate long labels
const truncateLabel = (text, maxLength) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 2) + '..';
};

function AnalyticsPanel({ activeTab, charts, summary, onClose, isExpanded, onToggleExpand, nlpData, allNLPData }) {
  const [infoPopup, setInfoPopup] = useState(null); // State for info popup
  const [viewMode, setViewMode] = useState('charts'); // 'charts' or 'nlp'
  const [showRefreshPopup, setShowRefreshPopup] = useState(false); // Refresh explanation popup

  // Use global NLP cache from context (shared across all tabs)
  const { getCached, loadingStates } = useNLPCache();

  // Use NLP processing hook for filtered refresh
  const { processFilteredTab, restoreFullResults, hasFullBackup } = useNLPProcessing();

  // Get cached results for current tab from global cache
  const pythonTFIDF = getCached(activeTab, 'tfidf');
  const pythonNER = getCached(activeTab, 'ner');
  const pythonTopics = getCached(activeTab, 'topics');
  const pythonTFIDFLoading = loadingStates[activeTab]?.tfidf || false;
  const pythonNERLoading = loadingStates[activeTab]?.ner || false;
  const pythonTopicsLoading = loadingStates[activeTab]?.topics || false;

  // NOTE: All NLP fetching is now done in LandingPage on data load
  // This component just displays the cached results from global context

  // Export chart as PNG using html2canvas
  const exportChartAsPNG = async (chartId, chartTitle) => {
    try {
      // Dynamically import html2canvas
      const html2canvas = (await import('html2canvas')).default;

      const chartElement = document.getElementById(`chart-${chartId}`);
      if (!chartElement) return;

      const canvas = await html2canvas(chartElement, {
        backgroundColor: '#ffffff',
        scale: 2 // Higher quality
      });

      // Convert to PNG and download
      const link = document.createElement('a');
      link.download = `${activeTab}_${chartTitle.replace(/\s+/g, '_')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  return (
    <div className="analytics-panel-overlay" onClick={onClose}>
      <div className={`analytics-panel ${isExpanded ? 'expanded' : ''}`} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="analytics-panel-header">
          <div>
            <h3>Analytics: {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</h3>
            <p className="analytics-subtitle">
              {summary.total.toLocaleString()} records
              {summary.filtered && ` (${summary.filtered.toLocaleString()} filtered)`}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              className="analytics-toggle-btn"
              onClick={onToggleExpand}
              aria-label={isExpanded ? "Collapse Panel" : "Expand Panel"}
              title={isExpanded ? "Side-by-side view" : "Expanded view"}
            >
              {isExpanded ? <FiMinimize2 size={18} /> : <FiMaximize2 size={18} />}
            </button>
            <button className="analytics-close-btn" onClick={onClose} aria-label="Close Analytics">
              <FiX size={24} />
            </button>
          </div>
        </div>

        {/* View Mode Tabs */}
        <div style={{
          display: 'flex',
          gap: '8px',
          borderBottom: '1px solid #e5e7eb',
          marginBottom: '16px',
          padding: '0 16px',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setViewMode('charts')}
              style={{
                padding: '12px 24px',
                background: 'none',
                border: 'none',
                borderBottom: viewMode === 'charts' ? '3px solid #2a5298' : '3px solid transparent',
                color: viewMode === 'charts' ? '#2a5298' : '#6b7280',
                fontWeight: viewMode === 'charts' ? 600 : 400,
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              📊 Charts & Metrics
            </button>
            <button
              onClick={() => setViewMode('nlp')}
              style={{
                padding: '12px 24px',
                background: 'none',
                border: 'none',
                borderBottom: viewMode === 'nlp' ? '3px solid #2a5298' : '3px solid transparent',
                color: viewMode === 'nlp' ? '#2a5298' : '#6b7280',
                fontWeight: viewMode === 'nlp' ? 600 : 400,
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              🔤 Text Analytics (NLP)
            </button>
          </div>

          {/* NLP Action Buttons (only shown in NLP view) */}
          {viewMode === 'nlp' && (
            <div style={{ display: 'flex', gap: '8px' }}>
              {/* Restore Full Results Button (only if backup exists) */}
              {hasFullBackup(activeTab) && (
                <button
                  onClick={() => restoreFullResults(activeTab)}
                  style={{
                    padding: '8px 16px',
                    background: 'linear-gradient(135deg, #2a5298 0%, #2a5298 100%)',
                    border: 'none',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
                    marginBottom: '-3px'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.4)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.3)';
                  }}
                  title="Restore full dataset NLP results"
                >
                  <FiRotateCcw size={14} />
                  Restore Full
                </button>
              )}

              {/* Refresh NLP Button */}
              <button
                onClick={() => setShowRefreshPopup(true)}
                style={{
                  padding: '8px 16px',
                  background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 4px rgba(249, 115, 22, 0.3)',
                  marginBottom: '-3px'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(249, 115, 22, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(249, 115, 22, 0.3)';
                }}
                title="Refresh NLP analysis with current filters"
              >
                <FiRefreshCw size={14} />
                Refresh NLP
              </button>
            </div>
          )}
        </div>

        {viewMode === 'charts' && (
          <>
            {/* Summary Cards */}
            {summary.cards && summary.cards.length > 0 && (
              <div className="analytics-summary-cards">
                {summary.cards.map((card, idx) => (
                  <div key={idx} className="analytics-summary-card">
                    <div className="card-label">{card.label}</div>
                    <div className="card-value">{card.value}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Charts Grid */}
            <div className="analytics-charts-grid">
          {charts.map((chart, idx) => (
            <div key={idx} className="analytics-chart-card">
              <div className="chart-card-header">
                <div className="chart-header-left">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <h4>{chart.title}</h4>
                    {chart.transformInfo && (
                      <button
                        className="chart-info-btn"
                        onClick={() => setInfoPopup(chart.transformInfo)}
                        title="View data transformations"
                        style={{
                          background: 'none',
                          border: '1px solid #2a5298',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          color: '#2a5298'
                        }}
                      >
                        <FiInfo size={14} />
                      </button>
                    )}
                  </div>
                  {chart.note && (
                    <div className="chart-note">
                      <FiInfo size={14} />
                      <span>{chart.note}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="chart-container" id={`chart-${chart.id}`}>
                {renderChart(chart)}
              </div>
            </div>
          ))}
        </div>
          </>
        )}

        {/* NLP View */}
        {viewMode === 'nlp' && nlpData && (
          <div className="nlp-analytics-container" style={{ padding: '16px' }}>
            {/* Filter & Processing Status Info */}
            <div style={{
              marginBottom: '20px',
              padding: '14px 16px',
              background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
              borderRadius: '8px',
              border: '2px solid #2a5298'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#2a5298' }}>
                  📊 Current View
                </div>
                {hasFullBackup(activeTab) && (
                  <div style={{
                    padding: '4px 10px',
                    background: '#fef3c7',
                    borderRadius: '4px',
                    border: '1px solid #f59e0b',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#92400e'
                  }}>
                    Filtered Results Active
                  </div>
                )}
              </div>
              <div style={{ fontSize: '12px', color: '#1e3a6e', lineHeight: '1.6' }}>
                <strong>{nlpData.sourceTexts?.length || 0} {activeTab}</strong> being analyzed
                {summary.filtered && ` (filtered from ${summary.total.toLocaleString()} total)`}
              </div>

              {/* Processing Status */}
              {(pythonTFIDFLoading || pythonNERLoading || pythonTopicsLoading) && (
                <div style={{
                  marginTop: '10px',
                  padding: '10px 12px',
                  background: 'white',
                  borderRadius: '6px',
                  border: '1px solid #93c5fd',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <div className="subtle-game-loader" style={{ transform: 'scale(0.6)' }}>
                    <div className="t-pixel"></div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#2a5298', marginBottom: '4px' }}>
                      Processing NLP for {activeTab}...
                    </div>
                    <div style={{ fontSize: '11px', color: '#4a7bc7' }}>
                      {pythonTFIDFLoading && '⏳ TF-IDF Analysis... '}
                      {pythonNERLoading && '⏳ Entity Extraction... '}
                      {pythonTopicsLoading && '⏳ Topic Discovery... '}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Word Clouds (Visual Overview) */}
            {nlpData.wordClouds && nlpData.wordClouds.length > 0 && (
              <div className="analytics-charts-grid">
                {nlpData.wordClouds.map((cloud, idx) => (
                  <div key={idx} className="analytics-chart-card">
                    <div className="chart-card-header">
                      <div className="chart-header-left">
                        <h4>{cloud.title}</h4>
                        {cloud.note && (
                          <div className="chart-note">
                            <FiInfo size={14} />
                            <span>{cloud.note}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="chart-container" style={{ height: '400px' }}>
                      {renderWordCloud(cloud.data)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Keyword Trends */}
            {nlpData.keywordTrends && nlpData.keywordTrends.trends && nlpData.keywordTrends.trends.length > 0 && (
              <div className="analytics-chart-card" style={{ gridColumn: '1 / -1' }}>
                <div className="chart-card-header">
                  <div className="chart-header-left">
                    <h4>Keyword Trends Over Time</h4>
                    <div className="chart-note">
                      <FiInfo size={14} />
                      <span>Top keywords tracked across time periods</span>
                    </div>
                  </div>
                </div>
                <div className="chart-container">
                  {renderKeywordTrendsChart(nlpData.keywordTrends)}
                </div>
              </div>
            )}

            {/* Python NLP Sections - Wrapped to prevent overlap with word clouds grid */}
            <div className="python-nlp-sections">
              {/* TF-IDF Analysis (Production-Grade) */}
              {pythonTFIDFLoading && !pythonTFIDF && (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                color: '#6b7280',
                background: '#f9fafb',
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>📊</div>
                <div style={{ fontSize: '14px', fontWeight: 500 }}>
                  Processing TF-IDF analysis...
                </div>
                <div style={{ fontSize: '12px', marginTop: '4px', opacity: 0.7 }}>
                  Analyzing {nlpData.sourceTexts?.length || 0} {activeTab} descriptions
                </div>
              </div>
            )}

            {pythonTFIDF && pythonTFIDF.length > 0 && (
              <div className="analytics-chart-card" style={{ marginBottom: '24px' }}>
                <div className="chart-card-header">
                  <div className="chart-header-left">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <h4>📊 TF-IDF Term Importance</h4>
                      <button
                        className="chart-info-btn"
                        onClick={() => setInfoPopup({
                          chartTitle: 'TF-IDF Term Importance',
                          interpretation: 'TF-IDF (Term Frequency-Inverse Document Frequency) identifies the most distinctive and important keywords in your dataset. Higher scores mean the term is frequent in these records but rare overall - making it highly characteristic of this specific group.',
                          procedures: [
                            '• TF (Term Frequency): How often a word appears in a document',
                            '• IDF (Inverse Document Frequency): How rare the word is across all documents',
                            '• TF-IDF Score = TF × IDF (higher = more distinctive)',
                            '• Filters out common words (the, and, of, etc.)',
                            '• Perfect for understanding what makes your data unique'
                          ],
                          sampleSize: `${nlpData.sourceTexts?.length || 0} ${activeTab} analyzed`
                        })}
                        title="Learn more about TF-IDF"
                        style={{
                          background: 'none',
                          border: '1px solid #7c3aed',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          color: '#7c3aed'
                        }}
                      >
                        <FiHelpCircle size={14} />
                      </button>
                    </div>
                    <div className="chart-note">
                      <FiInfo size={14} />
                      <span>
                        Top {pythonTFIDF.length} terms • {nlpData.sourceTexts?.length || 0} documents • Production-grade analysis
                      </span>
                    </div>
                  </div>
                </div>
                <div className="tfidf-table-container" style={{ overflowX: 'auto', padding: '16px' }}>
                  <table className="tfidf-table" style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '13px'
                  }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb', background: '#f8f9fa' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Rank</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Term</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>TF-IDF Score</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Doc Frequency</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>% of Docs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pythonTFIDF.map((term, idx) => (
                        <tr key={idx} style={{
                          borderBottom: '1px solid #e5e7eb',
                          background: idx % 2 === 0 ? '#fff' : '#f9fafb'
                        }}>
                          <td style={{ padding: '10px', color: '#6b7280', fontWeight: 600 }}>{idx + 1}</td>
                          <td style={{ padding: '10px', color: '#7c3aed', fontWeight: 600, fontSize: '14px' }}>
                            {term.term}
                          </td>
                          <td style={{ padding: '10px', textAlign: 'right', fontFamily: 'monospace', color: '#059669', fontWeight: 600 }}>
                            {term.score.toFixed(4)}
                          </td>
                          <td style={{ padding: '10px', textAlign: 'right', color: '#6b7280' }}>
                            {term.doc_freq}
                          </td>
                          <td style={{ padding: '10px', textAlign: 'right', color: '#6b7280' }}>
                            {term.percentage}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{
                    marginTop: '16px',
                    padding: '12px',
                    background: 'linear-gradient(135deg, #eff6ff 0%, #f3e8ff 100%)',
                    borderRadius: '8px',
                    fontSize: '12px',
                    color: '#6b7280',
                    borderLeft: '4px solid #7c3aed'
                  }}>
                    <strong style={{ color: '#6d28d9' }}>📊 TF-IDF Analysis</strong> - Production-grade analysis with advanced stopword filtering,
                    unicode normalization, and statistical precision. Higher scores = more distinctive terms for this dataset.
                  </div>
                </div>
              </div>
            )}

            {/* Named Entity Recognition (NER) */}
            {pythonNERLoading && !pythonNER && (
              <div style={{
                padding: '30px',
                textAlign: 'center',
                color: '#6b7280',
                background: '#f9fafb',
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>🏷️</div>
                <div style={{ fontSize: '13px' }}>Extracting named entities...</div>
              </div>
            )}

            {pythonNER && pythonNER.length > 0 && (
              <div className="analytics-chart-card" style={{ marginBottom: '24px' }}>
                <div className="chart-card-header">
                  <div className="chart-header-left">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <h4>🏷️ Named Entity Recognition</h4>
                      <button
                        className="chart-info-btn"
                        onClick={() => setInfoPopup({
                          chartTitle: 'Named Entity Recognition (NER)',
                          interpretation: 'NER automatically identifies and extracts important entities mentioned in your text - like company names, people, monetary amounts, products, and locations. This helps you quickly understand WHO and WHAT is being discussed without reading every description.',
                          procedures: [
                            '• ORG (Organizations): Company names, institutions',
                            '• PERSON: Names of people, founders, executives',
                            '• MONEY: Dollar amounts, funding figures',
                            '• PRODUCT: Product names, technologies',
                            '• GPE (Geopolitical): Cities, countries, regions',
                            '• Uses spaCy AI model trained on millions of documents',
                            '• Frequency counter shows how often each entity appears'
                          ],
                          sampleSize: `${pythonNER.length} entities found in ${nlpData.sourceTexts?.length || 0} ${activeTab}`
                        })}
                        title="Learn more about NER"
                        style={{
                          background: 'none',
                          border: '1px solid #2a5298',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          color: '#2a5298'
                        }}
                      >
                        <FiHelpCircle size={14} />
                      </button>
                    </div>
                    <div className="chart-note">
                      <FiInfo size={14} />
                      <span>
                        Organizations, People, Money, Products, and Locations extracted
                      </span>
                    </div>
                  </div>
                </div>
                <div style={{ padding: '16px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {pythonNER.slice(0, 40).map((entity, idx) => {
                    const labelColors = {
                      'ORG': '#2a5298',
                      'PERSON': '#8b5cf6',
                      'MONEY': '#10b981',
                      'PRODUCT': '#f59e0b',
                      'GPE': '#06b6d4',
                      'DATE': '#ec4899',
                      'PERCENT': '#14b8a6'
                    };
                    const color = labelColors[entity.label] || '#6b7280';

                    return (
                      <div key={idx} style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        background: '#fff',
                        border: `2px solid ${color}`,
                        borderRadius: '20px',
                        fontSize: '13px'
                      }}>
                        <span style={{ fontWeight: 600, color }}>{entity.text}</span>
                        <span style={{
                          fontSize: '10px',
                          padding: '2px 6px',
                          background: color,
                          color: '#fff',
                          borderRadius: '10px',
                          fontWeight: 600
                        }}>{entity.label}</span>
                        {entity.count > 1 && (
                          <span style={{
                            fontSize: '11px',
                            color: '#6b7280',
                            fontWeight: 600
                          }}>×{entity.count}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div style={{
                  margin: '16px',
                  padding: '12px',
                  background: 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: '#6b7280',
                  borderLeft: '4px solid #2a5298'
                }}>
                  <strong style={{ color: '#2a5298' }}>🏷️ Entity Recognition</strong> - Identifies organizations, people, monetary amounts, products, and locations mentioned in your data.
                </div>
              </div>
            )}

            {/* Topic Modeling (LDA) */}
            {pythonTopicsLoading && !pythonTopics && (
              <div style={{
                padding: '30px',
                textAlign: 'center',
                color: '#6b7280',
                background: '#f9fafb',
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>🎯</div>
                <div style={{ fontSize: '13px' }}>Discovering themes and topics...</div>
              </div>
            )}

            {pythonTopics && pythonTopics.length > 0 && (
              <div className="analytics-chart-card" style={{ marginBottom: '24px' }}>
                <div className="chart-card-header">
                  <div className="chart-header-left">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <h4>🎯 Topic Modeling</h4>
                      <button
                        className="chart-info-btn"
                        onClick={() => setInfoPopup({
                          chartTitle: 'Topic Modeling (LDA)',
                          interpretation: 'Topic Modeling uses AI to automatically discover hidden themes in your data by analyzing which words tend to appear together. Each "topic" is a cluster of related keywords that represent a common theme. Weight percentages show how prominent each theme is in your dataset.',
                          procedures: [
                            '• LDA (Latent Dirichlet Allocation): Industry-standard algorithm',
                            '• Analyzes word co-occurrence patterns across all documents',
                            '• Groups related keywords into coherent themes',
                            '• Weight = percentage of documents dominated by this topic',
                            '• Example: If "cloud, gaming, streaming" appear together often, they form a "cloud gaming" topic',
                            '• No manual tagging required - 100% automatic',
                            '• Helps identify market segments and trends'
                          ],
                          sampleSize: `${pythonTopics.length} topics discovered from ${nlpData.sourceTexts?.length || 0} ${activeTab}`
                        })}
                        title="Learn more about Topic Modeling"
                        style={{
                          background: 'none',
                          border: '1px solid #f59e0b',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          color: '#f59e0b'
                        }}
                      >
                        <FiHelpCircle size={14} />
                      </button>
                    </div>
                    <div className="chart-note">
                      <FiInfo size={14} />
                      <span>
                        {pythonTopics.length} themes discovered • Automatic clustering
                      </span>
                    </div>
                  </div>
                </div>
                <div style={{ padding: '16px' }}>
                  {pythonTopics.map((topic, idx) => (
                    <div key={idx} style={{
                      marginBottom: '16px',
                      padding: '16px',
                      background: idx % 2 === 0 ? '#f9fafb' : '#fff',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <h5 style={{ margin: 0, color: '#2a5298', fontSize: '14px', fontWeight: 700 }}>
                          Topic {topic.topic_id}
                        </h5>
                        <div style={{
                          padding: '4px 12px',
                          background: 'linear-gradient(135deg, #2a5298 0%, #8b5cf6 100%)',
                          color: '#fff',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 600
                        }}>
                          Weight: {(topic.weight * 100).toFixed(1)}%
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {topic.keywords.map((keyword, kidx) => (
                          <span key={kidx} style={{
                            padding: '4px 10px',
                            background: '#fff',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '12px',
                            color: '#374151',
                            fontWeight: 500
                          }}>
                            {keyword}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{
                  margin: '16px',
                  padding: '12px',
                  background: 'linear-gradient(135deg, #fef3c7 0%, #fce7f3 100%)',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: '#6b7280',
                  borderLeft: '4px solid #f59e0b'
                }}>
                  <strong style={{ color: '#92400e' }}>🎯 LDA Topic Modeling</strong> - Automatically discovers hidden themes by analyzing which words appear together. Higher weight = more prominent theme in your dataset.
                </div>
              </div>
            )}

              {/* No NLP Data Message */}
              {(!nlpData.wordClouds || nlpData.wordClouds.length === 0) && !pythonTFIDFLoading && !pythonTFIDF && (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: '#6b7280',
                  background: '#f9fafb',
                  borderRadius: '12px',
                  border: '2px dashed #d1d5db'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>🤖</div>
                  <p style={{ fontSize: '18px', marginBottom: '8px', color: '#374151', fontWeight: '600' }}>NLP Analysis Not Run Yet</p>
                  <p style={{ fontSize: '14px', marginBottom: '24px' }}>Automatic processing has been disabled to reduce latency and costs.</p>
                  {nlpData.sourceTexts && nlpData.sourceTexts.length > 0 ? (
                    <>
                      <p style={{ fontSize: '14px', marginBottom: '16px', color: '#059669' }}>
                        ✓ Ready to analyze {nlpData.sourceTexts.length} {activeTab}
                      </p>
                      <button
                        onClick={async () => {
                          await processFilteredTab(activeTab, nlpData.sourceTexts);
                        }}
                        style={{
                          padding: '12px 24px',
                          background: 'linear-gradient(135deg, #2a5298 0%, #7c3aed 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '16px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          transition: 'transform 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                        onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        <FiRefreshCw size={18} />
                        Run NLP Analysis Now
                      </button>
                      <p style={{ fontSize: '12px', marginTop: '12px', color: '#6b7280' }}>
                        This will analyze descriptions, keywords, and text fields using TF-IDF, NER, and LDA.
                      </p>
                    </>
                  ) : (
                    <p style={{ fontSize: '14px' }}>No text data available for NLP analysis on this tab.</p>
                  )}
                </div>
              )}
            </div> {/* End python-nlp-sections */}
          </div>
        )}

        {/* Info Popup Modal */}
        {infoPopup && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000
            }}
            onClick={() => setInfoPopup(null)}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: '12px',
                padding: '24px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '80vh',
                overflow: 'auto',
                boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                <h3 style={{ color: '#2a5298', fontSize: '18px', fontWeight: 700, margin: 0 }}>
                  {infoPopup.chartTitle}
                </h3>
                <button
                  onClick={() => setInfoPopup(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6b7280',
                    fontSize: '24px',
                    lineHeight: '1',
                    padding: '0'
                  }}
                >
                  ×
                </button>
              </div>

              {infoPopup.procedures && infoPopup.procedures.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                    Data Transformations Applied:
                  </h4>
                  {infoPopup.procedures.map((proc, idx) => (
                    <p key={idx} style={{ fontSize: '13px', margin: '6px 0', color: '#4b5563', lineHeight: '1.5' }}>
                      {proc}
                    </p>
                  ))}
                </div>
              )}

              {infoPopup.interpretation && (
                <div style={{ marginBottom: '16px', padding: '12px', background: '#eff6ff', borderRadius: '8px', borderLeft: '4px solid #2a5298' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#2a5298', marginBottom: '6px' }}>
                    Interpretation:
                  </h4>
                  <p style={{ fontSize: '13px', color: '#1e3a6e', margin: 0, lineHeight: '1.5' }}>
                    {infoPopup.interpretation}
                  </p>
                </div>
              )}

              {infoPopup.sampleSize && (
                <div style={{ padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
                  <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
                    <span style={{ fontWeight: 600 }}>Sample Size:</span> {infoPopup.sampleSize}
                  </p>
                </div>
              )}

              {/* Show issues/warnings if any */}
              {infoPopup.issues && infoPopup.issues.length > 0 && (
                <div style={{ marginTop: '12px', padding: '12px', background: '#fef3c7', borderRadius: '8px', borderLeft: '4px solid #f59e0b' }}>
                  <h4 style={{ fontSize: '13px', fontWeight: 600, color: '#92400e', marginBottom: '6px' }}>
                    ⚠️ Data Quality Notes:
                  </h4>
                  {infoPopup.issues.map((issue, idx) => (
                    <p key={idx} style={{ fontSize: '12px', color: '#78350f', margin: '4px 0' }}>
                      {issue}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Refresh NLP Explanation Popup */}
        {showRefreshPopup && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.6)',
              zIndex: 10001,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            }}
            onClick={() => setShowRefreshPopup(false)}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '12px',
                maxWidth: '500px',
                width: '100%',
                padding: '24px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
                maxHeight: '90vh',
                overflowY: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#1f2937' }}>
                  🔄 NLP Refresh Explained
                </h3>
                <button
                  onClick={() => setShowRefreshPopup(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#9ca3af',
                    padding: '0',
                    lineHeight: '1'
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '15px', color: '#4b5563', lineHeight: '1.6', marginBottom: '16px' }}>
                  NLP processing (TF-IDF, NER, Topics) is <strong>computationally expensive</strong> and takes time. Here's why we don't auto-refresh:
                </div>

                <div style={{
                  background: '#fef3c7',
                  border: '2px solid #f59e0b',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{ fontWeight: 700, color: '#92400e', marginBottom: '8px', fontSize: '14px' }}>
                    ⚠️ Performance Trade-off
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '20px', color: '#7c2d12', fontSize: '13px' }}>
                    <li>Processing 1,500+ deals takes 15-30 seconds</li>
                    <li>NER (Named Entity Recognition) is the slowest (analyzes each text individually)</li>
                    <li>Auto-refreshing on every filter change would freeze the UI</li>
                    <li>Background processing only runs once on data load</li>
                  </ul>
                </div>

                <div style={{
                  background: '#dbeafe',
                  border: '2px solid #2a5298',
                  borderRadius: '8px',
                  padding: '16px'
                }}>
                  <div style={{ fontWeight: 700, color: '#2a5298', marginBottom: '8px', fontSize: '14px' }}>
                    💡 Our Approach
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '20px', color: '#1e3a6e', fontSize: '13px' }}>
                    <li><strong>Initial Load:</strong> All NLP runs automatically in background</li>
                    <li><strong>Manual Refresh:</strong> You decide when to re-process with filtered data</li>
                    <li><strong>Smart Caching:</strong> Results persist across tab switches</li>
                    <li><strong>No Surprises:</strong> You control when to wait for updated insights</li>
                  </ul>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowRefreshPopup(false)}
                  style={{
                    padding: '10px 20px',
                    background: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#374151',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Got it
                </button>
                <button
                  onClick={async () => {
                    setShowRefreshPopup(false);
                    if (nlpData?.sourceTexts && nlpData.sourceTexts.length > 0) {
                      await processFilteredTab(activeTab, nlpData.sourceTexts);
                    }
                  }}
                  style={{
                    padding: '10px 20px',
                    background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                    border: 'none',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  🔄 Refresh Now
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper function to render word clouds
function renderWordCloud(data) {
  if (!data || data.length === 0) {
    return <div className="chart-empty-state">No text data available</div>;
  }

  return <WordCloudChart data={data} height={400} />;
}

// Helper function to render keyword trends
function renderKeywordTrendsChart(trendsData) {
  if (!trendsData.trends || trendsData.trends.length === 0) {
    return <div className="chart-empty-state">No trend data available</div>;
  }

  const { trends, keywords } = trendsData;

  return (
    <ResponsiveContainer width="100%" height={350}>
      <LineChart data={trends} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 11, fill: '#6b7280' }}
          label={{
            value: 'Time Period',
            position: 'insideBottom',
            offset: -10,
            style: { fontSize: 12, fill: '#374151' }
          }}
        />
        <YAxis
          tick={{ fontSize: 11, fill: '#6b7280' }}
          label={{
            value: 'Frequency (per 1000 words)',
            angle: -90,
            position: 'insideLeft',
            style: { fontSize: 12, fill: '#374151' }
          }}
        />
        <Tooltip />
        <Legend />
        {keywords.map((keyword, idx) => (
          <Line
            key={keyword}
            type="monotone"
            dataKey={keyword}
            stroke={COLORS[idx % COLORS.length]}
            strokeWidth={2}
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

function renderChart(chart) {
  const { type, data } = chart;

  if (!data || data.length === 0) {
    return <div className="chart-empty-state">No data available for this chart</div>;
  }

  switch (type) {
    case 'line':
      return (
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data} margin={{ top: 20, right: 30, left: 10, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: '#6b7280' }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
            <Tooltip
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '6px' }}
              formatter={(value) => [value.toLocaleString(), 'Count']}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={COLORS[0]}
              strokeWidth={3}
              dot={{ fill: COLORS[0], r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      );

    case 'bar':
      return (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data} margin={{ top: 30, right: 30, left: 10, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: '#6b7280' }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
            <Tooltip
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '6px' }}
              formatter={(value) => [value.toLocaleString(), 'Count']}
            />
            <Bar dataKey="value" fill={COLORS[0]} label={{ position: 'top', fontSize: 10, fill: '#374151' }}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );

    case 'horizontal-bar':
      return (
        <ResponsiveContainer width="100%" height={Math.max(300, data.length * 32)}>
          <BarChart data={data} layout="horizontal" margin={{ top: 20, right: 60, left: 120, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis type="number" tick={{ fontSize: 11, fill: '#6b7280' }} />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: 11, fill: '#6b7280' }}
              width={110}
            />
            <Tooltip
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '6px' }}
              formatter={(value) => [value.toLocaleString(), 'Count']}
            />
            <Bar dataKey="value" label={{ position: 'right', fontSize: 10, fill: '#374151' }}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );

    case 'pie':
      return (
        <ResponsiveContainer width="100%" height={350}>
          <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
            <Pie
              data={data}
              cx="50%"
              cy="45%"
              labelLine={{ stroke: '#9ca3af', strokeWidth: 1 }}
              label={({ name, value, percent }) => {
                // Only show label if slice > 3%
                if (percent < 0.03) return null;
                return `${truncateLabel(name, 12)}\n${(percent * 100).toFixed(1)}%`;
              }}
              outerRadius={100}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                  stroke="#fff"
                  strokeWidth={2}
                />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '6px' }}
              formatter={(value, name) => [value.toLocaleString(), name]}
            />
            <Legend
              wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }}
              iconType="circle"
            />
          </PieChart>
        </ResponsiveContainer>
      );

    case 'donut':
      return (
        <ResponsiveContainer width="100%" height={350}>
          <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
            <Pie
              data={data}
              cx="50%"
              cy="45%"
              labelLine={{ stroke: '#9ca3af', strokeWidth: 1 }}
              label={({ name, value, percent }) => {
                // Only show label if slice > 3%
                if (percent < 0.03) return null;
                return `${truncateLabel(name, 12)}\n${(percent * 100).toFixed(1)}%`;
              }}
              innerRadius={65}
              outerRadius={100}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                  stroke="#fff"
                  strokeWidth={2}
                />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '6px' }}
              formatter={(value, name) => [value.toLocaleString(), name]}
            />
            <Legend
              wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }}
              iconType="circle"
            />
          </PieChart>
        </ResponsiveContainer>
      );

    case 'scatter':
      const xLabel = chart.xLabel || 'X Axis';
      const yLabel = chart.yLabel || 'Y Axis';
      const regression = chart.regression;

      // Custom tooltip for scatter plots with all metrics
      const CustomScatterTooltip = ({ active, payload }) => {
        if (active && payload && payload.length > 0) {
          const data = payload[0].payload;
          return (
            <div style={{
              backgroundColor: '#fff',
              border: '2px solid #2a5298',
              borderRadius: '8px',
              padding: '12px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}>
              <p style={{ fontWeight: 700, marginBottom: '8px', color: '#2a5298' }}>
                {data.name}
              </p>
              {Object.keys(data).filter(key =>
                key !== 'name' && key !== 'x' && key !== 'y' && key !== 'z' &&
                !key.endsWith('_original') && typeof data[key] === 'number'
              ).map(key => (
                <p key={key} style={{ fontSize: '12px', margin: '4px 0', color: '#374151' }}>
                  <span style={{ fontWeight: 600 }}>
                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                  </span> {' '}
                  {data[key].toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </p>
              ))}
              {chart.useLogScale && (
                <p style={{ fontSize: '11px', marginTop: '6px', color: '#6b7280', fontStyle: 'italic' }}>
                  Values shown on log₁₀ scale
                </p>
              )}
            </div>
          );
        }
        return null;
      };

      return (
        <ResponsiveContainer width="100%" height={450}>
          <ScatterChart margin={{ top: 30, right: 40, left: 50, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              type="number"
              dataKey="x"
              tick={{ fontSize: 11, fill: '#6b7280' }}
              label={{
                value: xLabel,
                position: 'insideBottom',
                offset: -15,
                style: { fontSize: 12, fill: '#374151', fontWeight: 600 }
              }}
            />
            <YAxis
              type="number"
              dataKey="y"
              tick={{ fontSize: 11, fill: '#6b7280' }}
              label={{
                value: yLabel,
                angle: -90,
                position: 'insideLeft',
                style: { fontSize: 12, fill: '#374151', fontWeight: 600 }
              }}
            />
            <ZAxis dataKey="z" range={[30, 200]} /> {/* Bubble size from 'z' field */}
            <Tooltip content={<CustomScatterTooltip />} />

            {/* Trend line if regression available */}
            {regression && regression.trendLine && (
              <ReferenceLine
                segment={regression.trendLine.map(point => ({ x: point.x, y: point.y }))}
                stroke="#dc2626"
                strokeWidth={2}
                strokeDasharray="5 5"
                label={{
                  value: `R² = ${regression.r2.toFixed(3)}`,
                  position: 'top',
                  fill: '#dc2626',
                  fontSize: 11,
                  fontWeight: 600
                }}
              />
            )}

            {/* Scatter plot with bubble sizes */}
            <Scatter data={data} fill={COLORS[4]}>
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                  fillOpacity={0.7}
                />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      );

    case 'geo-heatmap':
      // For geo-heatmap, fall back to horizontal bar if no proper heatmap library
      // The data should have { name: 'Country', value: count, code: 'US' }
      // For now, render as a horizontal bar chart (can upgrade to actual map later)
      const geoData = data.length > 0 ? data : (chart.fallbackData || []);
      if (geoData.length === 0) {
        return (
          <div className="chart-empty-state">
            <p>No geographic data available</p>
            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
              Try using the /api/v1/deals/full endpoint to get hq_country data
            </p>
          </div>
        );
      }

      return (
        <ResponsiveContainer width="100%" height={Math.max(300, geoData.length * 32)}>
          <BarChart data={geoData.slice(0, 15)} layout="horizontal" margin={{ top: 20, right: 60, left: 120, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis type="number" tick={{ fontSize: 11, fill: '#6b7280' }} />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: 11, fill: '#6b7280' }}
              width={110}
            />
            <Tooltip
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '6px' }}
              formatter={(value) => [value.toLocaleString(), 'Deals']}
            />
            <Bar dataKey="value" label={{ position: 'right', fontSize: 10, fill: '#374151' }}>
              {geoData.slice(0, 15).map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );

    default:
      return <div className="chart-empty-state">Unknown chart type: {type}</div>;
  }
}

export default AnalyticsPanel;
